import { Brain, ListChecks } from 'lucide-react';
import { benTaubOr, BB_DOC } from './benTaubOr';

/*
 * Ben Taub Neuro rotation. Built from the Blackboard Neuro Orientation
 * (General OR Setup for Neurosurgical Cases). See docs/blackboard-ben-taub-subspecialties.md.
 * Contacts and Locations extend the General OR rotation.
 */

const plan = (rows) => ({ type: 'rows', rows });

export const benTaubNeuro = {
  accent: benTaubOr.accent,
  icon: Brain,
  tagline: 'Setup by case: tumor, bleed, aneurysm, spine',
  notesKey: 'notes:ben-taub:neuro',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'setup', title: 'Case Setup', subtitle: 'Blood, lines, position, anesthetic, ICP, disposition', icon: ListChecks, action: 'guide:setup', featured: true },
    ] },
  ],

  documents: {
    storageKey: 'ben-taub-neuro-pinned-docs',
    groups: [
      { id: 'neuro', title: 'Neuro', items: [
        { title: 'Neuro orientation', subtitle: 'Case setup guide and required reading. Blackboard', url: BB_DOC('_255073_1') },
      ] },
      ...benTaubOr.documents.groups,
    ],
  },

  guides: {
    setup: {
      title: 'Case Setup',
      segments: [
        { id: 'tumor', label: 'Tumor', sections: [
          { title: 'Intracranial tumor', blocks: [plan([
            { label: 'Blood', value: 'Type and screen', note: 'Type and cross if severely anemic' },
            { label: 'Lines', value: '2+ IVs. Arterial line after induction' },
            { label: 'Position', value: 'Supine or prone, pinned, table turned 180' },
            { label: 'Pinning', value: 'Propofol and esmolol' },
            { label: 'Maintenance', value: 'Volatile, sufentanil infusion, other infusions as needed' },
            { label: 'ICP', value: 'PaCO2 about 30, head up, mannitol 0.5–1 g/kg at incision' },
            { label: 'After', value: 'Extubate. ICU' },
          ])] },
        ] },
        { id: 'bleed', label: 'Bleed', sections: [
          { title: 'Intracranial bleed', blocks: [plan([
            { label: 'Blood', value: 'Type and cross RBCs', note: 'FFP and platelets may be needed' },
            { label: 'Lines', value: '2+ IVs. Arterial line after induction. Hotline' },
            { label: 'Position', value: 'Lazy lateral with a bump, no pins, table turned 180' },
            { label: 'Maintenance', value: 'Volatile and fentanyl', note: 'Usually under 2 h, no opioid infusion' },
            { label: 'ICP', value: 'PaCO2 about 30, head up, mannitol 0.5–1 g/kg at incision' },
            { label: 'After', value: 'Usually stays intubated. ICU' },
          ])] },
        ] },
        { id: 'aneurysm', label: 'Aneurysm', sections: [
          { title: 'Subarachnoid aneurysm', blocks: [plan([
            { label: 'Blood', value: 'Type and cross RBCs' },
            { label: 'Lines', value: '3+ IVs. Arterial line before induction. Hotline', note: 'Central line may be needed' },
            { label: 'Position', value: 'Supine, pinned, table turned 180' },
            { label: 'Pinning', value: 'Propofol and esmolol' },
            { label: 'Maintenance', value: 'Volatile, propofol and sufentanil infusions. BIS. Norepinephrine loaded on the pump' },
            { label: 'ICP', value: 'Normocapnia. No mannitol' },
            { label: 'After', value: 'Extubate. ICU' },
          ])] },
          { title: 'Clipping', blocks: [{ type: 'bullets', items: [
            'Before clipping, confirm with neurosurgery: temporary or permanent clip, and the blood pressure goal during clipping',
            'Burst suppression on BIS (1:10) may be requested before the clip',
            'After the clip: indocyanine green. Ask the circulator for it and the surgeon for the volume. Inject, then flush with saline right away',
            'Neuromonitoring (cranial nerve, SSEP, MEP): bite block, no long-acting paralytic',
          ] }] },
        ] },
        { id: 'spine', label: 'C-spine', sections: [
          { title: 'Cervical spine fixation', blocks: [plan([
            { label: 'Blood', value: 'Type and screen' },
            { label: 'Lines', value: '2+ IVs. Arterial line after induction' },
            { label: 'Position', value: 'Prone, pinned' },
            { label: 'Maintenance', value: 'Volatile under 0.5 MAC with neuromonitoring. Propofol and sufentanil infusions' },
            { label: 'Neuromonitoring', value: 'SSEP and MEP are common: bite block, no long-acting paralytic' },
            { label: 'Pressure', value: 'MAP above 80. Norepinephrine or phenylephrine drip is usual' },
            { label: 'After', value: 'Extubate. PACU' },
          ])] },
        ] },
      ],
      sources: [{ title: 'General OR setup for neurosurgical cases', href: BB_DOC('_255073_1'), detail: 'Blackboard, sign-in required' }],
    },
  },
};
